import type { AppNotification } from '../core/models/index';

export const NOTIF_CATEGORIES = {
  cutoff: {
    label: { en: 'Cutoffs', es: 'Cierres' },
    desc: {
      en: 'Alerts when a play window is about to close or just closed.',
      es: 'Avisos cuando una ventana de jugadas está por cerrar o acaba de cerrar.',
    },
  },
  results: {
    label: { en: 'Results', es: 'Resultados' },
    desc: {
      en: 'Draw results, winning plays, and prize notices.',
      es: 'Resultados del sorteo, jugadas ganadoras y avisos de premio.',
    },
  },
  limits: {
    label: { en: 'Limits', es: 'Límites' },
    desc: {
      en: 'Number caps reached, surge warnings, and collection alerts.',
      es: 'Topes por número alcanzados, picos de demanda y alertas de cobro.',
    },
  },
  routes: {
    label: { en: 'Routes', es: 'Rutas' },
    desc: {
      en: 'Runner route assignments, stops, and pickups.',
      es: 'Asignaciones de ruta del corredor, paradas y recogidas.',
    },
  },
  users: {
    label: { en: 'Users', es: 'Usuarios' },
    desc: {
      en: 'Staff and player account changes that need your attention.',
      es: 'Cambios de cuentas de personal o jugadores que requieren tu atención.',
    },
  },
  messages: {
    label: { en: 'Messages', es: 'Mensajes' },
    desc: {
      en: 'Direct messages and inbox conversations.',
      es: 'Mensajes directos y conversaciones del buzón.',
    },
  },
  integrity: {
    label: { en: 'Integrity', es: 'Integridad' },
    desc: {
      en: 'Fraud and integrity signals across houses.',
      es: 'Señales de fraude e integridad entre bancas.',
    },
  },
  system: {
    label: { en: 'System', es: 'Sistema' },
    desc: {
      en: 'Platform health, house status, and operational alerts.',
      es: 'Salud de la plataforma, estado de bancas y alertas operativas.',
    },
  },
  summary: {
    label: { en: 'Summary', es: 'Resumen' },
    desc: {
      en: 'End-of-shift digests and daily activity summaries.',
      es: 'Resúmenes de fin de turno y de actividad diaria.',
    },
  },
  billing: {
    label: { en: 'Billing', es: 'Facturación' },
    desc: {
      en: 'Renewals, failed payments and refunds on your subscription.',
      es: 'Renovaciones, pagos fallidos y devoluciones de tu suscripción.',
    },
  },
};

// Billing defaults on for whoever pays (banker) and for god oversight; the
// urgent dunning notices ignore the preference anyway.
export const DEFAULT_PREFS = {
  banker:  { cutoff:true,  results:true,  limits:true,  routes:true,  users:true,  messages:true, system:false, summary:true,  billing:true  },
  lister:  { cutoff:true,  results:true,  limits:false, routes:true,  users:true,  messages:true, system:false, summary:false, billing:false },
  runner:  { cutoff:false, results:false, limits:true,  routes:true,  users:true,  messages:true, system:false, summary:true,  billing:false },
  god:     { cutoff:false, results:false, limits:false, routes:false, users:false, messages:true, integrity:true, system:true,  summary:false, billing:true },
  support_agent: { cutoff:true, results:true, limits:true, routes:true, users:true, messages:true, system:true, summary:true, billing:false },
};

type NotifRole = keyof typeof DEFAULT_PREFS;
type NotifCategoryKey = keyof typeof NOTIF_CATEGORIES;

export function getNotifActions(
  cat: NotifCategoryKey | string,
  lang: string,
  role: NotifRole | string = 'banker',
  notif: Partial<AppNotification> | null = null,
) {
  const es = lang === 'es';
  const cutoffClosed = notif?.meta?.phase === 'closed' || notif?.meta?.focus === 'publishResults';

  const cutoffByRole = () => {
    if (cutoffClosed && role === 'banker') {
      return [
        {
          id: 'publish',
          label: es ? 'Publicar resultados' : 'Publish results',
          target: 'banker:overview',
          primary: true,
          meta: { focus: 'publishResults' },
        },
      ];
    }
    if (role === 'banker') {
      return [
        { id: 'review', label: es ? 'Revisar jugadas' : 'Review plays', target: 'banker:plays', primary: true },
        { id: 'overview', label: es ? 'Ver panel' : 'View overview', target: 'banker:overview' },
      ];
    }
    if (role === 'lister') {
      return [
        { id: 'inscribe', label: es ? 'Jugar' : 'Inscribe play', target: 'lister:newplay', primary: true },
      ];
    }
    if (role === 'runner') {
      return [
        { id: 'route', label: es ? 'Abrir ruta' : 'Open route', target: 'runner:route', primary: true },
      ];
    }
    if (role === 'support_agent') {
      return [
        { id: 'overview', label: es ? 'Ver panel' : 'View hub', target: 'support_agent:overview', primary: true },
      ];
    }
    return [];
  };

  const resultsByRole = () => {
    if (role === 'lister') {
      return [
        { id: 'prize', label: es ? 'Ver premio' : 'View prize', target: 'lister:plays', primary: true },
        { id: 'draw', label: es ? 'Ver sorteo' : 'View draw', target: 'lister:results' },
      ];
    }
    if (role === 'banker') {
      return [
        {
          id: 'publish',
          label: es ? 'Publicar resultados' : 'Publish results',
          target: 'banker:overview',
          primary: true,
          meta: { focus: 'publishResults' },
        },
        { id: 'plays', label: es ? 'Ver jugadas' : 'View plays', target: 'banker:plays' },
      ];
    }
    return [
      { id: 'overview', label: es ? 'Ver panel' : 'View overview', target: `${role}:overview`, primary: true },
    ];
  };

  const limitsByRole = () => {
    if (role === 'runner') {
      return [
        { id: 'route', label: es ? 'Abrir ruta' : 'Open route', target: 'runner:route', primary: true },
      ];
    }
    if (role === 'banker' || role === 'god') {
      return [
        { id: 'plays', label: es ? 'Ver jugadas' : 'View plays', target: 'banker:plays', primary: true },
        { id: 'config', label: es ? 'Ver topes' : 'Review caps', target: 'banker:config' },
      ];
    }
    if (role === 'lister') {
      return [
        { id: 'plays', label: es ? 'Ver jugadas' : 'View plays', target: 'lister:plays', primary: true },
      ];
    }
    return [
      { id: 'overview', label: es ? 'Ver panel' : 'View hub', target: 'support_agent:overview', primary: true },
    ];
  };

  const actions = {
    cutoff: cutoffByRole(),
    results: resultsByRole(),
    limits: limitsByRole(),
    routes: role === 'banker'
      ? [{ id: 'route', label: es ? 'Ver ruta' : 'View route', target: 'banker:route', primary: true }]
      : role === 'lister'
        ? [{ id: 'handoff', label: es ? 'Confirmar entrega' : 'Confirm handoff', target: 'lister:handoff', primary: true }]
        : [{ id: 'route', label: es ? 'Abrir ruta' : 'Open route', target: 'runner:route', primary: true }],
    users: [
      { id: 'users', label: es ? 'Ver usuario' : 'View user', target: 'banker:users', primary: true },
    ],
    messages: [
      { id: 'inbox', label: es ? 'Abrir buzón' : 'Open inbox', target: 'sheet:inbox', primary: true },
    ],
    integrity: [
      { id: 'integrity', label: es ? 'Ver trazas' : 'View traces', target: 'god:integrity', primary: true },
    ],
    system: (() => {
      const ticketId = notif?.meta?.ticketId;
      const target = notif?.meta?.target;
      if (ticketId || target === 'sheet:supportTicket') {
        if (role === 'support_agent' || role === 'god') {
          return [{
            id: 'tickets',
            label: es ? 'Ver ticket' : 'View ticket',
            target: role === 'god' ? 'god:tickets' : 'support_agent:tickets',
            primary: true,
            meta: ticketId ? { ticketId } : undefined,
          }];
        }
        return [{
          id: 'myTicket',
          label: es ? 'Ver mi ticket' : 'View my ticket',
          target: 'sheet:supportTicket',
          primary: true,
          meta: ticketId ? { ticketId } : undefined,
        }];
      }
      if (typeof target === 'string' && target) {
        return [{
          id: 'open',
          label: es ? 'Abrir' : 'Open',
          target,
          primary: true,
          meta: notif?.meta,
        }];
      }
      return role === 'support_agent'
        ? [{ id: 'tickets', label: es ? 'Ver tickets' : 'View tickets', target: 'support_agent:tickets', primary: true }]
        : [{ id: 'audit', label: es ? 'Ver log' : 'View log', target: 'god:audit', primary: true }];
    })(),
    summary: [],
    billing: role === 'god'
      ? [{ id: 'billing', label: es ? 'Ver facturación' : 'View billing', target: 'god:billing', primary: true }]
      : [{
        id: 'subscription',
        label: es ? 'Ver suscripción' : 'View subscription',
        target: 'sheet:subscription',
        primary: true,
      }],
  };
  return actions[cat as NotifCategoryKey] ?? [];
}

export function seedNotifs(role: NotifRole | string, lang: string): AppNotification[] {
  const es = lang === 'es';
  const seeds = {
    banker: [
      { id:'seed_b1', cat:'cutoff', sev:'warn', urgent:false, unread:true, t:'12:55',
        title: es ? 'Cierre en 5 minutos' : 'Closes in 5 minutes',
        body:  es ? 'Ventana de día · 13:30.' : 'Day window · 13:30.',
        meta: { phase: 'warning', target: 'banker:plays' } },
      { id:'seed_b2', cat:'limits', sev:'danger', urgent:false, unread:true, t:'12:42',
        title: es ? 'Tope alcanzado · № 88' : 'Cap reached · № 88',
        body:  es ? 'Exposición $130 / tope $100 (p5–p7).' : 'Exposure $130 / cap $100 (p5–p7).',
        meta: { target: 'banker:plays', playId: 'p6' } },
      { id:'seed_b3', cat:'results', sev:'info', urgent:false, unread:false, t:'Yesterday · 14:02',
        title: es ? 'Resultados publicados' : 'Results published',
        body: '07 · 23 · 88. Florida.',
        meta: { target: 'banker:overview', focus: 'publishResults' } },
      { id:'seed_b4', cat:'users', sev:'warn', urgent:false, unread:true, t:'11:05',
        title: es ? 'Solicitud de acuerdo pendiente' : 'Agreement request pending',
        body:  es ? 'Yuniel R. · revisión de comisión.' : 'Yuniel R. · commission review.',
        meta: { target: 'banker:users' } },
    ],
    lister: [
      { id:'seed_l_win', cat:'results', sev:'success', urgent:false, unread:true, t:'14:02',
        title: es ? '¡Una jugada tuya ganó!' : 'One of your plays won!',
        body: es ? 'fijo · 07 · $50 → $3,750' : 'fijo · 07 · $50 → $3,750',
        meta: { playId: 'p1', target: 'lister:plays' } },
      { id:'seed_l2', cat:'cutoff', sev:'warn', urgent:false, unread:true, t:'12:50',
        title: es ? 'Cierre en 10 minutos' : 'Closes in 10 minutes',
        body:  es ? 'Ventana de día · 13:30.' : 'Day window · 13:30.',
        meta: { phase: 'warning', target: 'lister:newplay' } },
      { id:'seed_l_handoff', cat:'routes', sev:'info', urgent:false, unread:true, t:'13:20',
        title: es ? 'Salida autorizada — prepara la entrega' : 'Departure authorized — prepare handoff',
        body: es
          ? 'El runner va de camino. Confirma el monto en Entrega.'
          : 'Runner is on the way. Confirm the amount under Handoff.',
        meta: { target: 'lister:handoff' } },
    ],
    runner: [
      { id:'seed_r1', cat:'routes', sev:'info', urgent:false, unread:true, t:'13:15',
        title: es ? 'Ruta activa · 3 paradas' : 'Active route · 3 stops',
        body:  es ? '$13,550 pendientes (s2–s5).' : '$13,550 remaining (s2–s5).',
        meta: { target: 'runner:route' } },
      { id:'seed_r2', cat:'limits', sev:'warn', urgent:false, unread:true, t:'12:55',
        title: es ? 'Cobro urgente' : 'Urgent collection',
        body:  es ? 'Marisol P. tiene $9,450 (parada s2).' : 'Marisol P. has $9,450 (stop s2).',
        meta: { target: 'runner:route' } },
    ],
    god: [
      { id:'seed_g1', cat:'system', sev:'danger', urgent:true, unread:true, t:'11:30',
        title: es ? 'Casa suspendida' : 'House suspended',
        body:  es ? 'Casa Miami (h3) · anomalía.' : 'Casa Miami (h3) · anomaly detected.',
        meta: { target: 'god:audit' } },
      { id:'seed_g2', cat:'integrity', sev:'warn', urgent:false, unread:true, t:'09:14',
        title: es ? 'Señal de integridad' : 'Integrity signal',
        body:  es ? 'Casa Vedado · cobro sin jugadas.' : 'Casa Vedado · collection without plays.',
        meta: { target: 'god:integrity' } },
    ],
    support_agent: [
      { id:'seed_s1', cat:'system', sev:'warn', urgent:false, unread:true, t:'Now',
        title: es ? 'Ticket crítico abierto' : 'Critical ticket open',
        body:  es ? 'tk1 · Login timeout · Casa Habana' : 'tk1 · Login timeout · Casa Habana',
        meta: { ticketId: 'tk1', target: 'support_agent:tickets' } },
    ],
  };
  return (seeds[role as NotifRole] ?? []).map((s) => ({ createdAt: Date.now(), ...s })) as AppNotification[];
}
